import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Select, useToast,
  Table, Thead, Tbody, Tr, Th, Td,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import { herramientasApi } from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type TipoReporte = 'general' | 'prestadas' | 'dañadas' | 'mantenimiento';
const TIPOS: { value: TipoReporte; label: string }[] = [
  { value: 'general', label: 'General (inventario completo)' },
  { value: 'prestadas', label: 'Prestadas / en préstamo' },
  { value: 'dañadas', label: 'Dañadas' },
  { value: 'mantenimiento', label: 'En mantenimiento' },
];

const ModalReportes: React.FC<Props> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [tipo, setTipo] = useState<TipoReporte>('general');
  const [rows, setRows] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);

  const cargar = async (t: TipoReporte) => {
    setCargando(true);
    try {
      const res: any = await herramientasApi.reportes(t);
      // El backend puede devolver un array o un objeto { data: [] }
      const lista = Array.isArray(res) ? res : res?.data || res?.items || [];
      setRows(lista);
    } catch (error: any) {
      setRows([]);
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo generar el reporte.', status: 'error', duration: 4000 });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (isOpen) cargar(tipo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tipo]);

  const columnas = rows.length ? Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== 'object') : [];

  const handleExport = () => {
    if (!rows.length) {
      toast({ title: 'Sin datos para exportar', status: 'info', duration: 3000 });
      return;
    }
    const filas = rows.map((r) => {
      const o: any = {};
      columnas.forEach((c) => { o[c] = r[c]; });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    XLSX.writeFile(wb, `Reporte_Herramientas_${tipo}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Reportes de herramientas</ModalHeader>
        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" flexWrap="wrap" gap={3}>
              <Select value={tipo} onChange={(e) => setTipo(e.target.value as TipoReporte)} size="sm" w={{ base: 'full', md: '320px' }}>
                {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
              <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExport} isDisabled={!rows.length}>
                Exportar Excel
              </Button>
            </HStack>

            <Box overflowX="auto" borderRadius="md" borderWidth="1px">
              <Table variant="simple" size="sm">
                <Thead bg="#5b182b">
                  <Tr>{columnas.map((c) => <Th key={c} color="white" fontSize="xs">{c}</Th>)}</Tr>
                </Thead>
                <Tbody>
                  {cargando ? (
                    <Tr><Td colSpan={Math.max(columnas.length, 1)} textAlign="center" py={6}><Text color="gray.500">Cargando...</Text></Td></Tr>
                  ) : rows.length === 0 ? (
                    <Tr><Td colSpan={Math.max(columnas.length, 1)} textAlign="center" py={6}><Text color="gray.400">Sin resultados</Text></Td></Tr>
                  ) : (
                    rows.map((r, idx) => (
                      <Tr key={r.id ?? idx} bg={idx % 2 === 0 ? 'white' : 'gray.50'}>
                        {columnas.map((c) => <Td key={c} fontSize="sm">{String(r[c] ?? '—')}</Td>)}
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Cerrar</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalReportes;
